import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { FlexColumn } from 'ui-components/components/flex';
import { Card } from 'ui-components/components/card';
import { Text, TextType } from 'ui-components/components/text';
import { PackingSlipFormatDropdown } from './components/packing-slip-format-dropdown';
import { PackingSlipOrderItemsBy } from './components/packing-slip-order-items-by';
import { PackingSlipSetting } from './components/packing-slip-setting';
import { useInventoryTrackingEnabled } from '../../../../../../hooks/use-seller-setting';
import { usePackingSlipsCustomizedFeatureGate } from '../../../../../../feature-gates/use-packing-slips-customized-feature-gate';

import packingSlipMessages from '../../../../printing/document-option-modal/packing-slip/messages';
import messages from './messages';
import style from './style.module.less';
import { FORM_FIELDS } from './constants';

/**
 * @param {{
 *   values: {
 *     packingSlipFormat: string,
 *     packingSlipOrderItemsBy: string,
 *     packingSlipIncludeStoreLogo: boolean,
 *     packingSlipIncludeItemPrice: boolean,
 *     packingSlipIncludeItemSku: boolean,
 *     packingSlipIncludeScanToViewBarcode: boolean,
 *     packingSlipIncludeWarehouseLocation: boolean,
 *     packingSlipShowDiscountsAsLineItems: boolean,
 *     packingSlipHideZeroQuantityItems: boolean
 *   },
 *   onChange: (field: string, value: any) => void
 * }} props
 * @returns {JSX.Element}
 */
export const PrintedPackingSlips = ({ values, onChange }) => {
  const isInventoryEnabled = useInventoryTrackingEnabled();
  const {
    printerPackingSlipsCustomizedFeature: isPackingSlipsCustomizationEnabled,
    disabledMessage: packingSlipsCustomizationDisabledMessage,
  } = usePackingSlipsCustomizedFeatureGate();

  const createSettingChangeHandler = useCallback(
    (field) => (value) => {
      onChange(field, value);
    },
    [onChange],
  );

  const packingSlipSettings = [
    {
      key: FORM_FIELDS.PACKING_SLIP_INCLUDE_STORE_LOGO,
      isChecked: values.packingSlipIncludeStoreLogo,
      title: packingSlipMessages.storeLogoTitle,
      description: packingSlipMessages.storeLogoDescription,
      disabled: !isPackingSlipsCustomizationEnabled,
    },
    {
      key: FORM_FIELDS.PACKING_SLIP_INCLUDE_ITEM_PRICE,
      isChecked: values.packingSlipIncludeItemPrice,
      title: packingSlipMessages.itemPriceTitle,
      description: packingSlipMessages.itemPriceDescription,
      disabled: !isPackingSlipsCustomizationEnabled,
    },
    {
      key: FORM_FIELDS.PACKING_SLIP_INCLUDE_ITEM_SKU,
      isChecked: values.packingSlipIncludeItemSku,
      title: packingSlipMessages.itemSkuTitle,
      description: packingSlipMessages.itemSkuDescription,
      disabled: !isPackingSlipsCustomizationEnabled,
    },
    {
      key: FORM_FIELDS.PACKING_SLIP_INCLUDE_SCAN_TO_VIEW_BARCODE,
      isChecked: values.packingSlipIncludeScanToViewBarcode,
      title: packingSlipMessages.scanToViewTitle,
      description: packingSlipMessages.scanToViewDescription,
      disabled: !isPackingSlipsCustomizationEnabled,
    },
    {
      key: FORM_FIELDS.PACKING_SLIP_INCLUDE_WAREHOUSE_LOCATION,
      isChecked: values.packingSlipIncludeWarehouseLocation,
      title: packingSlipMessages.itemLocationTitle,
      description: packingSlipMessages.itemLocationDescription,
      disabled: !isPackingSlipsCustomizationEnabled || !isInventoryEnabled,
    },
    {
      key: FORM_FIELDS.PACKING_SLIP_SHOW_DISCOUNTS_AS_LINE_ITEMS,
      isChecked: values.packingSlipShowDiscountsAsLineItems,
      title: packingSlipMessages.showDiscountsTitle,
      description: packingSlipMessages.showDiscountsDescription,
      disabled: !isPackingSlipsCustomizationEnabled,
    },
    {
      key: FORM_FIELDS.PACKING_SLIP_HIDE_ZERO_QUANTITY_ITEMS,
      isChecked: values.packingSlipHideZeroQuantityItems,
      title: packingSlipMessages.hideZeroQuantityTitle,
      description: packingSlipMessages.hideZeroQuantityDescription,
      disabled: !isPackingSlipsCustomizationEnabled,
    },
  ];

  return (
    <Card>
      <Text className={style['section-title']} textType={TextType.H4} value={<FormattedMessage {...messages.printedPackingSlipsTitle} />} />
      <FlexColumn className={style.wrapper}>
        <PackingSlipFormatDropdown onChange={onChange} value={values.packingSlipFormat} />
        <PackingSlipOrderItemsBy onChange={onChange} value={values.packingSlipOrderItemsBy} />
        <FlexColumn>
          {packingSlipSettings.map(({ key, isChecked, title, description, disabled }) => (
            <PackingSlipSetting
              key={key}
              description={description}
              disabled={disabled}
              isChecked={isChecked}
              name={key}
              onChange={createSettingChangeHandler(key)}
              title={title}
              tooltipMessage={packingSlipsCustomizationDisabledMessage}
            />
          ))}
        </FlexColumn>
      </FlexColumn>
    </Card>
  );
};
